import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass";
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass';
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass";
import { LUTPass } from "three/examples/jsm/postprocessing/LUTPass";
import { FilmPass } from 'three/examples/jsm/postprocessing/FilmPass';
import { DotScreenPass } from 'three/examples/jsm/postprocessing/DotScreenPass';
import { GlitchPass } from 'three/examples/jsm/postprocessing/GlitchPass';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader';
import { OutlinePass } from 'three/examples/jsm/postprocessing/OutlinePass';
import AppData from "../../app.data";
import { Loader } from "../../loaders/assets/loader";
import { isMoMPostProcessingTarget, normalizePostProcessingPasses } from "./render.pass.utils";
export { getRenderPassAssetUrls } from "./render.pass.assets";

const appData = new AppData();

export class PassFactory {
    loader: Loader;

    constructor() {
        this.loader = new Loader();
    }

    public dispose() {
        this.loader?.dispose();
        this.loader = null as any;
    }

    public async createPasses(
        composer: EffectComposer, 
        renderSettings: any, 
        camera: THREE.Camera, 
        renderQuality: string,
        canvasRect: DOMRect | null,
        onOutlinePassCreated: (pass: OutlinePass) => void,
        onBloomPassCreated: (pass: UnrealBloomPass) => void,
        onLutPassCreated: (pass: LUTPass) => void
    ) {
        // Clear existing passes
         while (composer.passes.length > 0) {
            composer.removePass(composer.passes[0]);
        }

        if (!appData.spaceScene || !camera || !(camera as any).isCamera) {
            return;
        }

        const postProcessing = normalizePostProcessingPasses(renderSettings?.postProcessing, {
            forceFxaa: isMoMPostProcessingTarget(appData.galleryData, appData.spaceData)
        });

        // Check if there's a LUT pass and load it first
        const lutPassConfig = postProcessing.find((pass: any) => pass.type === "lut");

        let lutTexture;
        if (lutPassConfig && lutPassConfig?.cubeMapUrl) {
            const lutFile = await this.loader.loadLUTCube(lutPassConfig.cubeMapUrl);
            // @ts-ignore
            lutTexture = lutFile.texture3D;
        }

        const isLQ = renderQuality === "LQ";

        for (let index = 0; index < postProcessing.length; index++) {
            const pass = postProcessing[index];
            switch (pass.type) {
                case "dotScreen":
                    if (isLQ) break;
                    const scale = pass.scale || 0.5;
                    const angle = pass.angle || 0.8;
                    const dotScreenPass = new DotScreenPass(new THREE.Vector2(0, 0), scale, angle);
                    dotScreenPass.renderToScreen = true;
                    dotScreenPass.material.transparent = true;

                    // @ts-ignore
                    dotScreenPass.name = "dotScreen";
                    composer.addPass(dotScreenPass);
                    break;
                case "noise":
                    if (isLQ) break;
                    const intensity = pass.amount ?? pass.threshold ?? 1;
                    const noisePass = new FilmPass(intensity, false);
                    // @ts-ignore
                    noisePass.name = "noise";
                    composer.addPass(noisePass);
                    break;
                case "glitch":
                    if (isLQ) break;
                    const glitchPass = new GlitchPass();
                    // @ts-ignore
                    glitchPass.name = "glitch";
                    composer.addPass(glitchPass);
                    break;
                case "render":
                    if (!appData.spaceScene || !camera || !(camera as any).isCamera) {
                        break;
                    }
                    const renderScene = new RenderPass(appData.spaceScene, camera);
                    renderScene.clearColor = new THREE.Color(0, 0, 0);
                    renderScene.clearAlpha = 0;
                    // @ts-ignore
                    renderScene.name = "render";
                    composer.addPass(renderScene);

                    // Add outline pass after main scene render
                    if (!isLQ && canvasRect) {
                        const outline = new OutlinePass(
                            new THREE.Vector2(canvasRect.width, canvasRect.height),
                            appData.spaceScene,
                            camera
                        );
                        outline.edgeStrength = 5.0;
                        outline.edgeGlow = 1.0;
                        outline.edgeThickness = 1.0;
                        outline.visibleEdgeColor.set('#ffffff');
                        outline.hiddenEdgeColor.set('#ffffff');
                        // @ts-ignore
                        outline.name = 'outline';
                        onOutlinePassCreated(outline);
                        // composer.addPass(outline);
                    }
                    break;
                case "fxaa":
                    const fxaa = new ShaderPass(FXAAShader);
                    // @ts-ignore
                    fxaa.name = "fxaa";
                    if (canvasRect) {
                        // @ts-ignore
                        fxaa.uniforms['resolution'].value.x = 1 / (canvasRect.width);
                        // @ts-ignore
                        fxaa.uniforms['resolution'].value.y = 1 / (canvasRect.height);
                    }
                    composer.addPass(fxaa);
                    break;
                case "bloom":
                    if (isLQ || !canvasRect) break;
                    const bloomPass = new UnrealBloomPass(
                        new THREE.Vector2(canvasRect.width * 2, canvasRect.height * 2),
                        1.5,
                        0.4,
                        0.85
                    );
                    bloomPass.threshold = pass.threshold || 0.75;
                    bloomPass.strength = pass.strength || 1.0;
                    bloomPass.radius = pass.radius || 0.5;
                    // @ts-ignore
                    bloomPass.name = "bloom";
                    onBloomPassCreated(bloomPass);
                    composer.addPass(bloomPass);
                    break;
                case "lut":
                    if (!isLQ) {
                        const lutPass = new LUTPass({});
                        if (lutTexture) { lutPass.lut = lutTexture; }
                        lutPass.intensity = pass.intensity || 1.0;
                        // @ts-ignore
                        lutPass.name = "LUT";
                        onLutPassCreated(lutPass);
                        composer.addPass(lutPass);
                    }
                    break;
                case "ssao":
                    break;
                case "renderArtwork":
                    break;
            }
        }

        const outputPass = new OutputPass();
        // composer.addPass(outputPass);
    }
}

